import { Component, ViewChild, AfterViewInit, Inject, OnDestroy} from '@angular/core';
import { AdditionalInfoImpl } from '../../../model/status/additional-info-impl';
import { ValueField } from 'src/app/settings/model/value-field';
import { FieldType } from 'src/app/settings/model/field-type';
import { DataSource } from 'src/app/shared/model/data-source';
import { DataSourceCreator } from 'src/app/shared/util/data-source-creator';
import { VehicleDevicesService } from '../../../service/vehicle-devices.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DataOptionsConnectorService } from 'src/app/shared/service/data-options-connector.service';
import { PrimeTableComponent } from 'src/app/shared/component/prime-table/prime-table.component';
import { StoreService } from 'src/app/store/service/store.service';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { first, map, mergeMap, tap } from 'rxjs/operators';
import { StatusesTableFormatter } from 'src/app/vehicles/util/statuses-table-formatter';
import { DOCUMENT } from '@angular/common';
import { getPhysicalDeviceTypes } from 'src/app/vehicles/model/device-type';
import { DataSourceCollection } from 'src/app/shared/model/data-source-collection';
import { UserTokenService } from 'src/app/common/service/user-token.service';
import { FieldsService } from 'src/app/settings/service/fields.service';
import { RECEIVE_TIME } from 'src/app/common/const';
import { VehicleLayoutViewComponent } from 'src/app/shared/component/vehicle-layout-view/vehicle-layout-view.component';
import { VehicleLayout } from 'src/app/vehicles/model/vehicle-layout';
import { DeviceLocationFormatter } from 'src/app/vehicles/util/device-location-formatter';
import { CellType } from 'src/app/settings/model/cell-type';

@Component({
  selector: 'app-devices-dialog',
  templateUrl: './devices-dialog.component.html',
  styleUrls: ['./devices-dialog.component.scss'],
  providers: [VehicleDevicesService, DataOptionsConnectorService]
})
export class DevicesDialogComponent implements AfterViewInit, OnDestroy {

  @ViewChild('table') table: PrimeTableComponent<AdditionalInfoImpl>;
  @ViewChild('vehicleLayoutView') vehicleLayoutView: VehicleLayoutViewComponent;
  additionalInfoImpls: AdditionalInfoImpl[];

  dataSubscription?: Subscription;
  private groupSubscription: Subscription;
  height: number = 100;
  private window: Window;
  layout: VehicleLayout;
  private layoutSubscription: Subscription;

  constructor(public dialogRef: MatDialogRef<DevicesDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: any,
    private dataSourceCreator: DataSourceCreator, private vehicleDevicesService: VehicleDevicesService,
    private storeService: StoreService, @Inject(DOCUMENT) document: Document, private userTokenService: UserTokenService,
    private fieldService: FieldsService) {
    this.vehicleDevicesService.setSideNumber(data.sideNumber);
    this.window = document.defaultView;
  }

  ngAfterViewInit(): void {
    this.layoutSubscription = this.vehicleLayoutView.layoutSubject.subscribe((layout: VehicleLayout) => {
      this.layout = layout;
      this.initTable();
      this.dataSubscription = this.storeService.connectToAllDataUpdates<AdditionalInfoImpl>(this.dataSourceName).pipe(first()).subscribe(data => {
        if (data.length > 0) {
          this.additionalInfoImpls = data;
          this.height = Math.min(this.window.innerHeight - 80, 100 * data.length);
        }
      });
      this.groupSubscription = this.userTokenService.reloadGroups$.pipe(mergeMap(() => this.fieldService.resetPhysicalDeviceTypesNames(true))).subscribe(
        () => {
          this.vehicleDevicesService.unsubscribeData();
          this.initTable();
        });
    });
    this.vehicleLayoutView.initLayout(this.data);
  }

  ngOnDestroy(): void {
    this.vehicleDevicesService.unsubscribeData();
    this.dataSubscription?.unsubscribe();
    this.groupSubscription.unsubscribe();
    this.layoutSubscription.unsubscribe();
  }

  private initTable(): void {
    this.createDataSourceCollectionForExpansionRows()
      .pipe(
        tap(dataSourceCollection => {
          const dataSource = this.createDataSource();
          dataSource.addTableCellFormatter('hasLocation', new DeviceLocationFormatter());
          dataSource.setTableRowFormatter(new StatusesTableFormatter());
          this.vehicleDevicesService.setMainDataSource(dataSource);
          this.vehicleDevicesService.setStatusesDataSourcesCollection(dataSourceCollection);
          this.vehicleDevicesService.setVehicleLayout(this.layout);
          this.vehicleDevicesService.subscribeData();
          setTimeout(() => {
            this.table?.init(dataSource, this.vehicleDevicesService);
            this.table?.setExpandableRowDataSources(dataSourceCollection);
          });
        })
      )
      .subscribe();
  }

  private createDataSource(): DataSource {
    return this.dataSourceCreator.create(this.dataSourceName, this.getValueFields());
  }

  private createDataSourceCollectionForExpansionRows(): Observable<DataSourceCollection> {
    const creatorObservables = getPhysicalDeviceTypes().map(type => this.dataSourceCreator.createForDeviceType(type));
    return forkJoin(creatorObservables)
      .pipe(
        map(dataSources => {
          const dataSourceCollection = {};
          dataSources.forEach(dataSource => dataSourceCollection[dataSource.name] = dataSource);
          return dataSourceCollection;
        })
      );
  }

  private getValueFields(): ValueField[] {
    return [
      Object.assign(new ValueField(), {
        name: 'devId',
        type: FieldType.INTEGER,
        primaryKey: true,
        visible: false
      }),
      Object.assign(new ValueField(), {
        name: 'id',
        type: FieldType.DEVICE_ID,
        visible: true
      }),
      Object.assign(new ValueField(), {
        name: 'deviceType',
        type: FieldType.TEXT,
        visible: true
      }),
      Object.assign(new ValueField(), {
        name: 'version',
        type: FieldType.TEXT,
        visible: true
      }),
      Object.assign(new ValueField(), {
        name: 'error',
        type: FieldType.BOOLEAN,
        visible: true
      }),
      Object.assign(new ValueField(), {
        name: RECEIVE_TIME,
        type: FieldType.DATETIME,
        visible: true
      }),
      Object.assign(new ValueField(), {
        name: 'hasLocation',
        type: FieldType.BOOLEAN,
        visible: this.layout,
        disableSort: true,
        translatable: false,
        cellType: CellType.ICON,
        options: 'location_on'
      })
    ];
  }

  get dataSourceName(): string {
    return `devices-${this.data.sideNumber}`;
  }

}
